import dayjs, { type Dayjs } from 'dayjs';
import { VisitaEstado } from './visitasStatus';

export const EDITABLE_VISIT_STATES = [
    VisitaEstado.SIN_ASIGNAR,
    VisitaEstado.PENDIENTE,
    VisitaEstado.PROGRAMADA,
    VisitaEstado.RECHAZADO
] as const;

export const PROCESADO_VISIT_STATE = 9;
export const COMPLETADA_VISIT_STATE = 11;

export const READ_ONLY_VISIT_STATES = [
    VisitaEstado.FALLIDA,
    VisitaEstado.CANCELADA,
    VisitaEstado.INFORMADO_SRT,
    VisitaEstado.PRESENTADO,
    PROCESADO_VISIT_STATE,
    COMPLETADA_VISIT_STATE
] as const;

type VisitEditabilitySource = {
    estado?: number | null;
    fechaLimite?: string | null;
    fechaVisita?: string | null;
    fechaProgramada?: string | null;
};

// Solo PROGRAMADA se acota por fecha ("solo podra editarse hasta un dia antes
// de la fecha en la que debe realizar la visita", ver HU MPRE-1647). El resto
// de los estados editables (SIN_ASIGNAR, PENDIENTE, RECHAZADO) tienen edicion
// completa sin restriccion de fecha.
const resolveProgramadaReferenceDate = (visit: VisitEditabilitySource) =>
    visit.fechaProgramada || visit.fechaLimite || null;

export const isVisitEditableState = (estado?: number | null) =>
    typeof estado === 'number' &&
    EDITABLE_VISIT_STATES.includes(estado as (typeof EDITABLE_VISIT_STATES)[number]);

export const isPendingVisitEditableByDate = (
    visitDate?: string | null,
    referenceDate: Dayjs = dayjs()
) => {
    if (!visitDate) return false;

    const parsedVisitDate = dayjs(visitDate);

    if (!parsedVisitDate.isValid()) return false;

    return parsedVisitDate.startOf('day').isAfter(referenceDate.startOf('day'));
};

export const canEditVisit = (
    visit?: VisitEditabilitySource | null,
    referenceDate: Dayjs = dayjs()
) => {
    if (!visit || !isVisitEditableState(visit.estado)) {
        return false;
    }

    if (visit.estado !== VisitaEstado.PROGRAMADA) {
        return true;
    }

    return isPendingVisitEditableByDate(resolveProgramadaReferenceDate(visit), referenceDate);
};

// Estados en los que "Ver detalle" se muestra siempre en el menu de acciones.
const SEE_DETAIL_FIXED_STATES = [
    VisitaEstado.SIN_ASIGNAR,
    VisitaEstado.PENDIENTE,
    VisitaEstado.RECHAZADO
] as const;

// PROGRAMADA solo mantiene "Ver detalle" mientras siga siendo editable (fecha
// futura, ver canEditVisit); una vez vencida esa ventana la accion relevante
// pasa a ser "Ver visita" (ejecucion), igual que el resto de los estados
// posteriores. Reusar canEditVisit aca evita que ambas reglas se desincronicen.
export const canSeeVisitDetail = (
    visit?: VisitEditabilitySource | null,
    referenceDate: Dayjs = dayjs()
) => {
    if (!visit || visit.estado === undefined || visit.estado === null) {
        return false;
    }

    if (SEE_DETAIL_FIXED_STATES.includes(visit.estado as (typeof SEE_DETAIL_FIXED_STATES)[number])) {
        return true;
    }

    if (visit.estado === VisitaEstado.PROGRAMADA) {
        return canEditVisit(visit, referenceDate);
    }

    return false;
};

export const resolveVisitDetailsAction = (
    visit?: VisitEditabilitySource | null,
    isAdminBO?: boolean,
    referenceDate: Dayjs = dayjs()
) => {
    if (isAdminBO && canEditVisit(visit, referenceDate)) {
        return 'edit';
    }

    return 'read-only';
};

export const canPreventorRegisterVisit = (estado?: number | null) =>
    estado === VisitaEstado.PENDIENTE ||
    estado === VisitaEstado.PROGRAMADA ||
    estado === VisitaEstado.EN_PROCESO;
